import { Box, Container, css, Grid, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ConsultingStatusMutate } from '@/components/form-elements/ConsultingStatusMutate';
import { FormSection } from '@/components/form-elements/FormSection';
import { IndicatorProcessStatusIcon } from '@/components/icons/IndicatorProcessStatusIcon';
import { StatusChip } from '@/components/icons/StatusChip';
import { FullHeightLayout } from '@/components/layouts/FullHeightLayout';
import { useQueryOrganization } from '@/hooks/queries/useQueryOrganization';
import { useAppAbility } from '@/hooks/useAppAbility';
import { useIsFieldDisabled } from '@/hooks/useIsFieldDisabled';
import { LocationTable } from '@/organization/LocationTable';
import { Action, Location, Subject } from '@/services/api';

const StyledFullHeightLayout = styled(FullHeightLayout)(
  ({ theme }) => css`
    ${theme.breakpoints.up('lg')} {
      background: linear-gradient(to right, ${theme.palette.primary.light} calc(50% - 112px), transparent 0);
    }
  `,
);

export function OverviewPage() {
  const { t } = useTranslation();
  const ability = useAppAbility();

  const { data: organization } = useQueryOrganization();
  const isFieldDisabled = useIsFieldDisabled(organization);

  return (
    <StyledFullHeightLayout>
      <Container>
        <Grid container columnSpacing={24} alignItems="center">
          <Grid item lg={5}>
            <Typography variant="h1" gutterBottom>
              {organization?.name}
            </Typography>
            <Typography sx={{ marginBottom: 5 }}>
              Das Beratungsangebot „Besser zur Arbeit“ besteht aus den Beratungen vor Ort zur Erarbeitung eines
              passgenauen Mobilitätskonzepts und aus der Beratungsplattform zur Unterstützung und Verstetigung des
              Prozesses.
              <br />
              <br />
              Ergänzt wird das Angebot durch die Informationsplattform mit Wissenswertem über das Betriebliche
              Mobilitätsmanagement und einem regelmäßigen Workshopangebot.
              <br />
              <br />
              Die Angebote sollen Sie bei der Umsetzung von Maßnahmen und der Wirkungsanalyse unterstützen.
            </Typography>
          </Grid>
          <Grid item lg={7}>
            <FormSection title="Status" ml={false}>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                Der Status Ihres Beratungsprozesses ist
                <Box ml={2}>
                  {organization &&
                    (isFieldDisabled('status') ? (
                      <StatusChip label={t(`ConsultingStatus.${organization.status}`)} />
                    ) : (
                      <ConsultingStatusMutate organization={organization} />
                    ))}
                </Box>
              </Typography>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                {organization?.processes && (organization?.processes?.length || 0) > 0 ? (
                  <>
                    Der Status der aktuellsten Wohnstandortanalyse ist
                    <Box ml={2}>
                      <StatusChip
                        icon={
                          ability.can(Action.READ_ALL, Subject.INDICATOR_PROCESS) ? (
                            <IndicatorProcessStatusIcon
                              status={organization.processes[organization.processes.length - 1].status}
                            />
                          ) : undefined
                        }
                        label={t(
                          `${
                            ability.can(Action.READ_ALL, Subject.INDICATOR_PROCESS)
                              ? 'IndicatorProcessStatus'
                              : 'IndicatorProcessStatusPublic'
                          }.${organization.processes[organization.processes.length - 1].status}`,
                        )}
                      />
                    </Box>
                  </>
                ) : (
                  <>Bisher wurde keine Wohnstandortanalyse erstellt.</>
                )}
              </Typography>
            </FormSection>
            <LocationTable
              locations={organization?.locations?.map((location) => ({ ...location, organization }))}
              createEnabled={ability.can(Action.CREATE, {
                __caslSubjectType__: Subject.LOCATION,
                organization,
              } as Location)}
            />
          </Grid>
        </Grid>
      </Container>
    </StyledFullHeightLayout>
  );
}
